import type { NextPage } from "next";
import { createClient } from "../../prismicio";
import { SliceZone } from "@prismicio/react";
import { PrismicDocumentWithUID } from "@prismicio/types";
import { components } from "../slices/index";
import React from "react";
import { getSlice } from "../utils/sliceHelper";
import { useRouter } from "next/router";
import LDJson from "../components/ldjson";
import MetaFields from "../components/MetaFields";

interface IPreviewData {
  ref?: string;
}

interface IPageProps {
  params: any;
  locale?: string;
  previewData: IPreviewData;
}

export async function getStaticProps({ locale, previewData }: IPageProps) {
  let page: PrismicDocumentWithUID;
  let uid;
  const client = await createClient({ previewData });
  let mapLocale = "nl-nl";

  try {
    uid = "home";
    mapLocale = "nl" === locale ? "nl-nl" : "en-gb";
    page = await client.getByUID("pages", uid, { lang: mapLocale });
  } catch (e: any) {
    console.error(e);
    return {
      notFound: true,
    };
  }

  // Get the projects from the highlighted projects slices repeatable zone
  await Promise.all(
    page.data.slices.map(async (slice: any) => {
      if (slice.slice_type === "highlighted_projects") {
        const projects = await client.getAllByUIDs(
          "pages",
          slice.items.map((item: any) => item.projectLink.uid),
          {
            lang: mapLocale,
          }
        );
        slice.projects = projects;
      }
    })
  );

  const globalSettings = await client.getSingle("global_settings", {
    lang: mapLocale,
  });

  const jobs = await client.getAllByTag("Vacatures", {
    lang: mapLocale,
  });
  // console.log(globalSettings, mapLocale);
  const navigation = getSlice(
    globalSettings.data.slices,
    "slice_type",
    "navigation"
  );
  const footer = getSlice(globalSettings.data.slices, "slice_type", "footer");
  const services = getSlice(
    globalSettings.data.slices,
    "slice_type",
    "services"
  );
  // @ts-ignore
  const footerSections = globalSettings.data.slices.filter((slice: any) => {
    return slice
      ? ["location", "contact", "links", "socials"].includes(slice.slice_type)
      : null;
  });

  return {
    props: {
      page,
      jobsCount: jobs?.length || 9,
      services,
      footer,
      navigation,
      globalSettings,
      footerSections,
      env: {
        SITE_URL: process.env.SITE_URL,
        ROBOTS: process.env.ROBOTS,
      },
    },
  };
}

const Page: NextPage = ({
  page,
  jobsCount,
  footer,
  globalSettings,
  footerSections,
  env,
}: any) => {
  const separate = ["header", "breadcrumb"];
  const page_router = useRouter();

  return (
    <>
      {/* These will be added to <head> */}
      <MetaFields
        page={page}
        globalSettings={globalSettings}
        pageRouter={page_router}
        env={env}
      />
      <LDJson page={page} globals={globalSettings} />
      {/* </head> */}

      <header className="flex flex-col mb-14 lg:mb-24">
        <SliceZone
          slices={page.data.slices.filter((slice: any) =>
            separate.includes(slice.slice_type)
          )}
          components={{ ...components }}
          context={{ globalSettings, jobsCount }}
        />
      </header>
      <main
        className={`overflow-x-hidden flex flex-col px-8 space-y-14 mb-14 lg:space-y-32 lg:mb-32`}
      >
        <SliceZone
          slices={page.data.slices.filter(
            (slice: any) => !separate.includes(slice.slice_type)
          )}
          components={{ ...components }}
          context={{ globalSettings }}
        />
      </main>
      <footer className="bg-brand-marine px-8">
        <SliceZone
          slices={[footer]}
          components={{ ...components }}
          context={{ footer, globalSettings, footerSections }}
        />
      </footer>
    </>
  );
};
export default Page;
